import React from "react"
import type { FC } from "react"
import { useTranslation } from "react-i18next"
import { useLocation } from "react-router"

import { NzDataGrid, defaultRowHeight } from "@/components/nzDataGrid"
import { PageCard } from "@/components/page-card"
import { PageTitle } from "@/components/page-title"
import { useActiveOrganizationId } from "@/contexts"
import {
  createAdminGridEditAction,
  createAdminGridViewAction,
  useAdminGridActionsColumn,
} from "@/modules/admin/components/admin-grid-actions-column"
import {
  type MeterGridRecord,
  meterToGridRecord,
  useAdminMetersGridColumns,
} from "@/modules/admin/meters"
import {
  ADMIN_METERS_GRID_URL_KEYS,
  getAppliedAdminMetersFilters,
  getNextAdminMetersGridUrlUpdate,
  readAdminMetersGridUrl,
  toAdminMetersFilterModel,
} from "@/modules/admin/meters/admin-meters-grid-url"
import {
  DataGridFill,
  DataGridFilterPanel,
  DataGridToolbar,
  gridUrlValuesKey,
  useAdjustScrollOnPrepend,
  useDataGridVirtualScrollListener,
  useGridUrlState,
  useInfiniteScroll,
  useServerMappedStringFilters,
  useSyncInfiniteQueryPage,
  withGridUrlUpdates,
} from "@/nzds/data-grid"
import { useMetersInfinite } from "@/services/meter"
import { usePage } from "@/utils/page/use-page"
import { useUrl } from "@/utils/url"

import Stack from "@mui/material/Stack"
import type {
  GridFilterModel,
  GridSlotProps,
  GridSlots,
} from "@mui/x-data-grid-pro"

/**
 * Page the meters query should open on. A filter change starts at page 1 on
 * this render, before the address replace, so the loaded page is not written
 * back over that reset. Copying the address into the panel keeps its page.
 */
const resolveMetersQueryPage = ({
  filterSignature,
  page,
  skipSignatureChange,
  trackedSignature,
}: {
  filterSignature: string
  page: number
  skipSignatureChange: boolean
  trackedSignature: string
}): number =>
  filterSignature !== trackedSignature && !skipSignatureChange ? 1 : page

const syncAdminMetersGridFromUrl = ({
  criteria,
  filterSignature,
  gridUrlValues,
  lastWrittenKeyRef,
  logicOperator,
  page,
  replaceFilterModel,
  replaceGridUrlState,
  seededKeyRef,
  setSkipSignatureChange,
  setTrackedSignature,
  skipSignatureChange,
  trackedSignature,
}: {
  criteria: readonly { field: string; operator: string; value?: string }[]
  filterSignature: string
  gridUrlValues: Record<string, string | null>
  lastWrittenKeyRef: { current: string | null }
  logicOperator: string
  page: number
  replaceFilterModel: (model: GridFilterModel) => void
  replaceGridUrlState: (updates: Record<string, string | null>) => void
  seededKeyRef: { current: string }
  setSkipSignatureChange: (value: boolean) => void
  setTrackedSignature: (value: string) => void
  skipSignatureChange: boolean
  trackedSignature: string
}): void => {
  const currentKey = gridUrlValuesKey(ADMIN_METERS_GRID_URL_KEYS, gridUrlValues)

  if (currentKey === lastWrittenKeyRef.current) {
    lastWrittenKeyRef.current = null
    seededKeyRef.current = currentKey
    if (filterSignature !== trackedSignature) {
      setTrackedSignature(filterSignature)
    }
    return
  }

  // The Meters nav clears this route's query without remounting the page.
  if (currentKey !== seededKeyRef.current) {
    seededKeyRef.current = currentKey
    setSkipSignatureChange(true)
    replaceFilterModel(
      toAdminMetersFilterModel(readAdminMetersGridUrl(gridUrlValues))
    )
    return
  }

  const signatureChanged =
    !skipSignatureChange && filterSignature !== trackedSignature
  setSkipSignatureChange(false)

  const updates = getNextAdminMetersGridUrlUpdate({
    applied: getAppliedAdminMetersFilters(criteria, logicOperator),
    page,
    signatureChanged,
    values: gridUrlValues,
  })
  if (!updates) {
    if (filterSignature !== trackedSignature) {
      setTrackedSignature(filterSignature)
    }
    return
  }

  lastWrittenKeyRef.current = gridUrlValuesKey(
    ADMIN_METERS_GRID_URL_KEYS,
    withGridUrlUpdates(gridUrlValues, updates)
  )
  replaceGridUrlState(updates)
}

export const AdminMetersRoute: FC = () => {
  const { t } = useTranslation("common")
  const { search } = useLocation()
  const { activeOrganizationId } = useActiveOrganizationId()
  const { adminMeterEditPath, adminMeterPath } = useUrl()
  const gridRootRef = React.useRef<HTMLDivElement | null>(null)
  const { replaceGridUrlState, values: gridUrlValues } = useGridUrlState(
    ADMIN_METERS_GRID_URL_KEYS
  )
  const [initialFilterModel] = React.useState(() =>
    toAdminMetersFilterModel(readAdminMetersGridUrl(gridUrlValues))
  )
  const { page, setPage } = usePage({
    id: "adminMetersPage",
  })
  const {
    criteria,
    filterModel,
    filterSignature,
    logicOperator,
    replaceFilterModel,
    setFilterModel,
    whereClause: meterWhereClause,
  } = useServerMappedStringFilters({
    fieldToApiField: {
      code: "meter_code",
      provider: "meter_provider",
      siteName: "site_name",
    },
    fields: ["code", "siteName", "provider"],
    initialFilterModel,
    mode: "multiple",
    orFilterKey: "string_filters_or",
  })
  const [trackedSignature, setTrackedSignature] =
    React.useState(filterSignature)
  const [skipSignatureChange, setSkipSignatureChange] = React.useState(false)
  const gridUrlKey = gridUrlValuesKey(ADMIN_METERS_GRID_URL_KEYS, gridUrlValues)
  const gridUrlValuesRef = React.useRef(gridUrlValues)
  React.useLayoutEffect(() => {
    gridUrlValuesRef.current = gridUrlValues
  })
  const seededGridUrlKeyRef = React.useRef(gridUrlKey)
  const lastWrittenGridUrlKeyRef = React.useRef<string | null>(null)
  const metersQueryPage = resolveMetersQueryPage({
    filterSignature,
    page,
    skipSignatureChange,
    trackedSignature,
  })

  React.useEffect(() => {
    syncAdminMetersGridFromUrl({
      criteria,
      filterSignature,
      gridUrlValues: gridUrlValuesRef.current,
      lastWrittenKeyRef: lastWrittenGridUrlKeyRef,
      logicOperator,
      page,
      replaceFilterModel,
      replaceGridUrlState,
      seededKeyRef: seededGridUrlKeyRef,
      setSkipSignatureChange,
      setTrackedSignature,
      skipSignatureChange,
      trackedSignature,
    })
  }, [
    criteria,
    filterSignature,
    gridUrlKey,
    logicOperator,
    page,
    replaceFilterModel,
    replaceGridUrlState,
    skipSignatureChange,
    trackedSignature,
  ])

  const isMetersQueryEnabled = Boolean(activeOrganizationId)

  const {
    metersData,
    metersFetchNextPage,
    metersFetchPreviousPage,
    metersHasNextPage,
    metersHasPreviousPage,
    metersIsFetching,
    metersIsFetchingNextPage,
    metersIsFetchingPreviousPage,
    metersIsPlaceholderData,
  } = useMetersInfinite({
    apiQueryOptions: {
      includes: ["site", "meter_source_type"],
      where: { organization_id: activeOrganizationId, ...meterWhereClause },
    },
    initialPage: metersQueryPage,
    queryOptions: {
      enabled: isMetersQueryEnabled,
      throwOnError: false,
    },
  })

  const meters = React.useMemo(() => {
    if (!metersData) {
      return []
    }
    return metersData.pages.flatMap((pageData) => pageData.items)
  }, [metersData])

  const pages = metersData?.pages ?? []

  useSyncInfiniteQueryPage({
    page: metersQueryPage,
    pages,
    setPage,
  })

  const actionsColumn = useAdminGridActionsColumn<MeterGridRecord>({
    getActions: ({ id }) => {
      const meterId = String(id)
      return [
        createAdminGridViewAction(
          `${adminMeterPath(meterId)}${search}`,
          t("button.view")
        ),
        createAdminGridEditAction(
          `${adminMeterEditPath(meterId)}${search}`,
          t("button.edit")
        ),
      ]
    },
    minWidth: 120,
    width: 120,
  })
  const dataColumns = useAdminMetersGridColumns({
    fields: [
      "code",
      "siteName",
      "provider",
      "sourceTypeName",
      "status",
      "accountNumber",
    ],
    filterable: true,
    preserveSearch: true,
  })
  const columns = [...dataColumns, actionsColumn]

  const {
    handleOnRowsScrollEnd,
    handleOnScrollTopChange,
    handleOnWheelUpAtTop,
  } = useInfiniteScroll({
    fetchNextPage: metersFetchNextPage,
    fetchPreviousPage: metersFetchPreviousPage,
    hasNextPage: metersHasNextPage,
    hasPreviousPage: metersHasPreviousPage,
    isEnabled: isMetersQueryEnabled,
    isFetchingNextPage: metersIsFetchingNextPage,
    isFetchingPreviousPage: metersIsFetchingPreviousPage,
  })

  const { adjustScrollTopBy } = useDataGridVirtualScrollListener({
    gridRootRef,
    onScroll: handleOnScrollTopChange,
    onWheelUpAtTop: handleOnWheelUpAtTop,
  })

  const rows: MeterGridRecord[] = React.useMemo(() => {
    if (!meters.length) {
      return []
    }
    return meters.map((meter) => meterToGridRecord(meter))
  }, [meters])

  useAdjustScrollOnPrepend({
    adjustScrollTopBy,
    isFetchingPreviousPage: metersIsFetchingPreviousPage,
    rowCount: rows.length,
    rowHeight: defaultRowHeight,
  })

  return (
    <Stack sx={{ gap: 2, height: "100%", minHeight: 0 }}>
      <PageTitle heading={t("admin.meters")} />
      <PageCard
        sx={{
          display: "flex",
          flex: 1,
          flexDirection: "column",
          minHeight: 0,
        }}
      >
        <DataGridFill ref={gridRootRef}>
          <NzDataGrid
            aria-label={t("admin.metersGrid.ariaLabel")}
            columns={columns}
            disableColumnSorting
            filterModel={filterModel}
            getRowId={(row: MeterGridRecord) => row.id}
            filterMode="server"
            loading={
              metersIsFetching ||
              metersIsFetchingNextPage ||
              metersIsFetchingPreviousPage
            }
            onFilterModelChange={setFilterModel}
            onRowsScrollEnd={handleOnRowsScrollEnd}
            initialState={{ pinnedColumns: { right: ["actions"] } }}
            rows={rows}
            slotProps={{
              toolbar: {
                isRecordCountLoading:
                  metersIsFetching || metersIsPlaceholderData,
                recordCount: metersData?.pages[0]?.total,
                showFilters: true,
              } as GridSlotProps["toolbar"],
            }}
            slots={{
              filterPanel: DataGridFilterPanel as GridSlots["filterPanel"],
              toolbar: DataGridToolbar as GridSlots["toolbar"],
            }}
            sortingMode="server"
          />
        </DataGridFill>
      </PageCard>
    </Stack>
  )
}
